// EN-4743. Camera settings for the back of a licence / state ID, where a PDF417
// has to be read. Only the licence / state ID back camera uses these.
//
// A PDF417 module (one bar) is ~0.25 mm and a decoder needs about 2 camera pixels
// per module (the kiosk's note in IDCaptureModal.js SCAN_TUNING). At the ~1280 px
// a phone gives by default, that only happens with the card ~10 cm away, where many
// phones can't focus. So, like the kiosk (1920x1440 video, 2x zoom, max-resolution
// photo pass):
//  - ask for the highest resolution the camera offers (ideal 4K);
//  - continuous autofocus and a moderate zoom where the browser allows them, so
//    the card can be held ~20 cm away, inside the lens's focus range;
//  - a torch the visitor can turn on where the phone has one.
// Every setting is optional: a browser that doesn't support one simply keeps the
// camera's default, and nothing here throws into the page.

/** Back camera for a licence / state ID: as much resolution as the camera has. */
export const BARCODE_VIDEO_CONSTRAINTS: MediaTrackConstraints = {
  facingMode: 'environment',
  width: { ideal: 3840 },
  height: { ideal: 2160 },
  frameRate: { ideal: 30 },
};

// Digital / optical zoom while scanning. Moderate on purpose: the card should
// still fit the guide at ~20 cm on a typical ~68° rear lens. Tune from the field test.
export const SCAN_ZOOM = 1.5;

// Capabilities are not in every TypeScript DOM lib yet (focusMode, zoom, torch).
interface RangeCapability {
  min?: number;
  max?: number;
  step?: number;
}
interface ScanCapabilities {
  focusMode?: string[];
  zoom?: RangeCapability;
  torch?: boolean;
}

/** What was applied, for telemetry. Nothing here identifies the visitor. */
export interface CameraTuning {
  width: number;
  height: number;
  focusMode: string | null;
  zoom: number | null;
  torchAvailable: boolean;
}

const capabilitiesOf = (track: MediaStreamTrack): ScanCapabilities => {
  try {
    return (track.getCapabilities?.() ?? {}) as ScanCapabilities;
  } catch {
    return {};
  }
};

const apply = async (track: MediaStreamTrack, constraint: Record<string, unknown>): Promise<boolean> => {
  try {
    await track.applyConstraints({ advanced: [constraint as MediaTrackConstraintSet] });
    return true;
  } catch {
    return false;
  }
};

/**
 * Applies autofocus and zoom where supported, and reports what the camera is
 * doing. Safe on any track: unsupported settings are skipped.
 */
export const tuneBackCamera = async (track: MediaStreamTrack | null | undefined): Promise<CameraTuning> => {
  const result: CameraTuning = { width: 0, height: 0, focusMode: null, zoom: null, torchAvailable: false };
  if (!track) return result;
  const caps = capabilitiesOf(track);

  if (caps.focusMode?.includes('continuous') && (await apply(track, { focusMode: 'continuous' }))) {
    result.focusMode = 'continuous';
  }

  const zoom = caps.zoom;
  if (zoom && typeof zoom.max === 'number' && zoom.max > 1) {
    const target = Math.min(Math.max(SCAN_ZOOM, zoom.min ?? 1), zoom.max);
    if (await apply(track, { zoom: target })) result.zoom = target;
  }

  result.torchAvailable = caps.torch === true;

  try {
    const settings = track.getSettings?.() ?? {};
    result.width = settings.width ?? 0;
    result.height = settings.height ?? 0;
  } catch {
    // Settings are only for telemetry.
  }
  return result;
};

/** Turns the torch on or off. False when the phone refused. */
export const setTorch = (track: MediaStreamTrack | null | undefined, on: boolean): Promise<boolean> =>
  track ? apply(track, { torch: on }) : Promise.resolve(false);

/** The live video track of a <video> fed by getUserMedia, if any. */
export const videoTrackOf = (video: HTMLVideoElement | null | undefined): MediaStreamTrack | null => {
  const stream = video?.srcObject;
  if (!stream || typeof (stream as MediaStream).getVideoTracks !== 'function') return null;
  return (stream as MediaStream).getVideoTracks()[0] ?? null;
};

export type StillSource = 'photo-still' | 'photo-frame';

// ImageCapture isn't in every TypeScript DOM lib yet.
type ImageCaptureCtor = new (track: MediaStreamTrack) => { takePhoto: () => Promise<Blob> };

const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T> =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('timeout')), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });

/**
 * The best picture of the card for the last-chance read, taken while the camera
 * is still on screen: the sensor's full-resolution still where the browser can
 * take one (ImageCapture.takePhoto(), Chrome; 8-12 MP on a phone, like the
 * kiosk's photoResolution 'max'), otherwise the current video frame at the
 * stream's full resolution. Null if neither works.
 */
export const grabFullResolutionStill = async (
  video: HTMLVideoElement | null | undefined,
  timeoutMs = 2500,
): Promise<{ image: ImageBitmap; source: StillSource } | null> => {
  const track = videoTrackOf(video);
  const ImageCaptureImpl = (globalThis as { ImageCapture?: ImageCaptureCtor }).ImageCapture;
  if (track && track.readyState === 'live' && typeof ImageCaptureImpl === 'function') {
    try {
      const blob = await withTimeout(new ImageCaptureImpl(track).takePhoto(), timeoutMs);
      return { image: await createImageBitmap(blob), source: 'photo-still' };
    } catch {
      // Unsupported on this camera, refused or too slow: use the video frame.
    }
  }
  try {
    if (video && video.videoWidth > 0) return { image: await createImageBitmap(video), source: 'photo-frame' };
  } catch {
    // Nothing to read from; the JPEG photo is the last resort.
  }
  return null;
};
